import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import toast from 'react-hot-toast';

type RoleOption = { name: string; label: string };
type SelectOption = { value: string; label: string };

type FormOptions = {
    roles: RoleOption[];
    locales: SelectOption[];
    timezones: SelectOption[];
};

type User = {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    department: string | null;
    job_title: string | null;
    locale: string | null;
    timezone: string | null;
    bio: string | null;
    avatar_url: string | null;
    role_names: string[];
};

type FormState = {
    name: string;
    email: string;
    password: string;
    phone: string;
    department: string;
    job_title: string;
    locale: string;
    timezone: string;
    bio: string;
    roles: string[];
    avatar: File | null;
};

const empty = (): FormState => ({
    name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    job_title: '',
    locale: '',
    timezone: '',
    bio: '',
    roles: [],
    avatar: null,
});

export default function UserFormPage() {
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [form, setForm] = useState<FormState>(empty);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [preview, setPreview] = useState<string | null>(null);

    const optionsQuery = useQuery({
        queryKey: ['users', 'form-options'],
        queryFn: async (): Promise<FormOptions> => (await api.get('/users-form-options')).data,
        staleTime: 5 * 60_000,
    });

    const userQuery = useQuery({
        queryKey: ['users', id, 'edit'],
        enabled: isEdit,
        queryFn: async (): Promise<User> => (await api.get(`/users/${id}`)).data.data,
    });

    useEffect(() => {
        if (userQuery.data) {
            const u = userQuery.data;
            setForm((prev) => ({
                ...prev,
                name: u.name ?? '',
                email: u.email ?? '',
                phone: u.phone ?? '',
                department: u.department ?? '',
                job_title: u.job_title ?? '',
                locale: u.locale ?? '',
                timezone: u.timezone ?? '',
                bio: u.bio ?? '',
                roles: u.role_names ?? [],
            }));
            setPreview(u.avatar_url ?? null);
        }
    }, [userQuery.data]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const toggleRole = (name: string) =>
        setForm((prev) => ({
            ...prev,
            roles: prev.roles.includes(name) ? prev.roles.filter((r) => r !== name) : [...prev.roles, name],
        }));

    const onAvatarChange = (file: File | null) => {
        set('avatar', file);
        setPreview(file ? URL.createObjectURL(file) : userQuery.data?.avatar_url ?? null);
    };

    const buildPayload = () => {
        const fd = new FormData();
        const append = (k: string, v: any) => {
            if (v === null || v === undefined || v === '') return;
            fd.append(k, v);
        };

        if (!isEdit || form.name) append('name', form.name);
        if (!isEdit || form.email) append('email', form.email);
        if (form.password) append('password', form.password);
        append('phone', form.phone);
        append('department', form.department);
        append('job_title', form.job_title);
        append('locale', form.locale);
        append('timezone', form.timezone);
        append('bio', form.bio);
        form.roles.forEach((r, i) => fd.append(`roles[${i}]`, r));
        if (form.avatar) fd.append('avatar', form.avatar);
        if (isEdit) fd.append('_method', 'PUT');
        return fd;
    };

    const mutation = useMutation({
        mutationFn: async () => {
            const fd = buildPayload();
            const url = isEdit ? `/users/${id}` : '/users';
            return (await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
        },
        onSuccess: (res: any) => {
            setError(null);
            setFieldErrors({});
            qc.invalidateQueries({ queryKey: ['users'] });
            const newId = res?.data?.id ?? id;
            navigate(`/users/${newId}`);
        },
        onError: (err: any) => {
            const status = err?.response?.status;
            if (status === 422) {
                setFieldErrors(err.response.data?.errors ?? {});
                setError('Verifique os campos destacados.');
            } else {
                setError(err?.response?.data?.message ?? err?.message ?? 'Erro ao salvar.');
            }
        },
    });

    if (isEdit && userQuery.isLoading) return <LoadingState label="Carregando usuário…" />;

    const opts = optionsQuery.data;
    const fe = (k: string) => fieldErrors[k]?.[0];

    return (
        <div className="space-y-6">
            <PageHeader title={isEdit ? `Editar usuário · ${userQuery.data?.name ?? ''}` : 'Novo usuário'}>
                <div className="flex items-center gap-2">
                    <Link to={isEdit ? `/users/${id}` : '/users'}>
                        <Button variant="outline" size="sm" as="span">Cancelar</Button>
                    </Link>
                    <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                        {mutation.isPending ? 'Salvando…' : 'Salvar'}
                    </Button>
                </div>
            </PageHeader>

            {error && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <Section title="Perfil">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="flex flex-col items-center gap-3">
                        <div className="size-28 overflow-hidden rounded-full border border-gray-200 bg-gray-50">
                            {preview ? (
                                <img src={preview} alt="Avatar" className="size-full object-cover" />
                            ) : (
                                <div className="flex size-full items-center justify-center text-3xl text-gray-300">?</div>
                            )}
                        </div>
                        <FileDropzone
                            accept="image/png,image/jpeg,image/webp,image/*"
                            maxSizeMb={5}
                            label="Arraste a foto"
                            hint="PNG/JPG/WebP · clique para selecionar"
                            showSelected={false}
                            className="w-full [&>label]:py-4"
                            onFiles={(files) => onAvatarChange(files[0] ?? null)}
                            onError={(msg) => toast.error(msg)}
                        />
                    </div>

                    <div className="md:col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field label="Nome" required error={fe('name')}>
                            <input className={inp} value={form.name} onChange={(e) => set('name', e.target.value)} />
                        </Field>
                        <Field label="E-mail" required error={fe('email')}>
                            <input type="email" className={inp} value={form.email} onChange={(e) => set('email', e.target.value)} />
                        </Field>
                        <Field label="Telefone" error={fe('phone')}>
                            <input className={inp} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                        </Field>
                        <Field
                            label={isEdit ? 'Senha (deixe em branco para manter)' : 'Senha'}
                            required={!isEdit}
                            error={fe('password')}
                        >
                            <input type="password" className={inp} value={form.password} onChange={(e) => set('password', e.target.value)} />
                        </Field>
                    </div>
                </div>
            </Section>

            <Section title="Informações profissionais">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Cargo" error={fe('job_title')}>
                        <input className={inp} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} />
                    </Field>
                    <Field label="Departamento" error={fe('department')}>
                        <input className={inp} value={form.department} onChange={(e) => set('department', e.target.value)} />
                    </Field>
                    <Field label="Idioma" error={fe('locale')}>
                        <select className={inp} value={form.locale} onChange={(e) => set('locale', e.target.value)}>
                            <option value="">Padrão do sistema</option>
                            {opts?.locales.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Fuso horário" error={fe('timezone')}>
                        <select className={inp} value={form.timezone} onChange={(e) => set('timezone', e.target.value)}>
                            <option value="">Padrão do sistema</option>
                            {opts?.timezones.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="Sobre" error={fe('bio')}>
                            <textarea className={`${inp} min-h-24`} value={form.bio} onChange={(e) => set('bio', e.target.value)} />
                        </Field>
                    </div>
                </div>
            </Section>

            <Section title="Funções (roles)">
                {!opts?.roles?.length ? (
                    <p className="text-sm text-gray-500">Nenhuma role configurada. Use o Shield em /admin/v1.</p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {opts.roles.map((role) => {
                            const active = form.roles.includes(role.name);
                            return (
                                <button
                                    key={role.name}
                                    type="button"
                                    onClick={() => toggleRole(role.name)}
                                    className={
                                        active
                                            ? 'rounded-full border-2 border-amber-500 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-900'
                                            : 'rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700 hover:border-amber-300'
                                    }
                                >
                                    {role.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </Section>
        </div>
    );
}

const inp =
    'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
            {children}
        </section>
    );
}

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-600">
                {label} {required && <span className="text-red-500">*</span>}
            </span>
            {children}
            {error && <span className="block text-xs text-red-600">{error}</span>}
        </label>
    );
}
